import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { AuthLayout, Field } from "./AuthLayout";
import { PremiumInput } from "../../components/brand/PremiumInput";
import { useAuth } from "../../data/authStore";
import { apiError } from "../../lib/api";

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Enter the email on your account");
      return;
    }
    setSubmitting(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSent(true);
      toast.success("Reset link sent", { description: "Check your inbox for the next step." });
    } catch (e) {
      const msg = apiError(e);
      setError(msg);
      toast.error("Could not send reset link", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout
        testid="forgot-password-sent"
        eyebrow="Almost there"
        title="Check your inbox"
        subtitle={`If an account exists for ${email}, we've sent a reset link. The link expires in one hour.`}
        footer={<Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="forgot-back-to-login">← Back to sign in</Link>}
      >
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 flex items-start gap-3" data-testid="forgot-success-card">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 cf-shadow-md">
            <CheckCircle2 size={18} strokeWidth={2.4} />
          </div>
          <div>
            <p className="text-[13.5px] font-bold text-emerald-900">Reset email on its way</p>
            <p className="text-[12px] text-emerald-800/80 mt-1 leading-snug">
              Didn't get it? Check spam, or <button onClick={() => setSent(false)} className="underline font-semibold" data-testid="forgot-retry">try a different email</button>.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      testid="forgot-password-page"
      eyebrow="Account recovery"
      title="Reset your password"
      subtitle="Enter the email on your account and we'll send a secure reset link."
      footer={<Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="forgot-back-to-login">← Back to sign in</Link>}
    >
      <form onSubmit={onSubmit} data-testid="forgot-form" noValidate>
        <Field label="Email">
          <PremiumInput
            type="email"
            autoComplete="email"
            size="lg"
            value={email}
            onChange={setEmail}
            placeholder="you@agency.co.uk"
            testid="forgot-email"
            autoFocus
          />
        </Field>
        {error && <p className="text-[12px] text-rose-600 -mt-1 mb-3" data-testid="forgot-error">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className={`btn btn-primary !h-11 !px-5 !text-[13.5px] w-full ${submitting ? "opacity-60 cursor-not-allowed !shadow-none" : ""}`}
          data-testid="forgot-submit"
        >
          {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Mail size={14} /> Send reset link</>}
        </button>
      </form>
    </AuthLayout>
  );
}
